import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  #buyButtonsIntersectionObserver = null;
  #mainBottomObserver = null;
  #resetTimeout;
  #isStuck = false;
  #animationTimeout = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #currentQuantity = 1;
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#syncWithMainButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();

        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
          this.#syncWithMainButton();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
            this.#syncWithMainButton();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#syncWithMainButton();
  }

  #syncWithMainButton() {
    if (!this.#targetAddToCartButton || !this.refs.addToCartButton) return;

    const stickyButton = this.refs.addToCartButton;
    const mainButton = this.#targetAddToCartButton;

    if (mainButton.disabled) {
      stickyButton.disabled = true;
      stickyButton.textContent = mainButton.textContent.trim() || 'Redan i varukorgen';
      return;
    }

    stickyButton.disabled = false;
  }

  handleAddToCartClick = async () => {
    this.#syncWithMainButton();

    if (!this.#targetAddToCartButton) return;

    if (this.#targetAddToCartButton.disabled || this.refs.addToCartButton.disabled) {
      this.#syncWithMainButton();
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
      this.#syncWithMainButton();
    }, 800);
  };

  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();

    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
    this.#syncWithMainButton();
  };

  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    this.dataset.currentVariantId = variantId;
  };

  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => option.value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  #handleCartAddComplete = async () => {
  if (!this.dataset.productId) return;

  const response = await fetch('/cart.js');
  const cart = await response.json();

  const inCart = cart.items.some(
    (item) => item.product_id == this.dataset.productId
  );

  if (this.#targetAddToCartButton) {
    this.#targetAddToCartButton.dataset.puppet = 'false';

    this.#targetAddToCartButton.disabled = inCart;

    this.#targetAddToCartButton.textContent = inCart
      ? 'Redan i varukorgen'
      : 'Lägg till i varukorgen';
  }

  if (this.refs.addToCartButton) {
    this.refs.addToCartButton.disabled = inCart;

    this.refs.addToCartButton.textContent = inCart
      ? 'Redan i varukorgen'
      : 'Lägg till i varukorgen';
  }

  this.#syncWithMainButton();
};

  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
    this.#syncWithMainButton();
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncWithMainButton();
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}